import { useState } from "react";
import { MessageCircleQuestion, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ColoredOutlineButton, OUTLINE_TEXT_BUTTON_ICON_CLASS } from "@/components/ui/colored-outline-button";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/useAuth";
import { useListing } from "@/hooks/useListing";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";
import { toast } from "sonner";

export interface ListingInquiryCardProps {
  listingId: string;
  className?: string;
}

export function ListingInquiryCard({
  listingId,
  className = "",
}: ListingInquiryCardProps) {
  const { user } = useAuth();
  const { submitListingInquiryAsync, isSubmittingListingInquiry } = useListing();
  const { language } = useLanguage();
  const li = carDetailsLabels(language).listingInquiry;
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");

  const handleSend = async () => {
    const q = question.trim();
    if (!q) return;
    try {
      await submitListingInquiryAsync({ listingId, message: q });
      toast.success(li.successToast);
      setOpen(false);
      setQuestion("");
    } catch {
      toast.error(li.errorToast);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className={className}>
      <ColoredOutlineButton
        type="button"
        variant="blue"
        tintedBackground
        wrapperClassName="w-full"
        innerClassName="w-full min-h-12 h-auto py-3.5 text-sm md:min-h-9 md:h-9 md:py-2"
        onClick={() => setOpen(true)}
      >
        <MessageCircleQuestion
          className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
          strokeWidth={2.35}
          aria-hidden
        />
        {li.cta}
      </ColoredOutlineButton>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader className="pb-1">
            <DialogTitle className="pb-3">{li.dialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={li.placeholder}
              rows={5}
              className="resize-y min-h-[120px]"
              disabled={isSubmittingListingInquiry}
            />
            <p className="text-xs text-muted-foreground leading-relaxed">
              {li.hintScope}{" "}
              <Link
                to="/support"
                className="text-primary underline underline-offset-2 font-medium"
              >
                {li.supportLink}
              </Link>
              {li.hintSupportSuffix}
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setOpen(false)}
              disabled={isSubmittingListingInquiry}
            >
              {li.cancel}
            </Button>
            <Button
              type="button"
              className="bg-primary"
              disabled={!question.trim() || isSubmittingListingInquiry}
              onClick={handleSend}
            >
              {isSubmittingListingInquiry ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {li.sending}
                </>
              ) : (
                li.send
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
